/**
 * The slim "get the app" bar at the top of the page, for an Android phone's browser (P0211). iPhone and iPad get Apple's
 * own banner from public/index.html instead; see lib/getTheApp.js for when this one shows. It sits in the page (not
 * pinned), so the page's own sticky header still sticks once it scrolls away. Closed once, it stays closed on that phone.
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { IS_NATIVE } from '@/lib/nativeContext';
import { BAR_CLOSED_KEY, phoneKind, playStoreUrl, showsAndroidBar } from '@/lib/getTheApp';
import { EVENTS, track } from '@/lib/analytics';

const readClosed = () => {
  try {
    return localStorage.getItem(BAR_CLOSED_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const isStandalone = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch (_) {
    return false;
  }
};

export default function GetTheAppBar() {
  const location = useLocation();
  const [closed, setClosed] = useState(readClosed);

  const shows = showsAndroidBar({
    kind: typeof navigator === 'undefined' ? 'other' : phoneKind(navigator),
    native: IS_NATIVE,
    standalone: isStandalone(),
    closed,
    path: location.pathname,
  });
  if (!shows) return null;

  const close = () => {
    setClosed(true);
    try {
      localStorage.setItem(BAR_CLOSED_KEY, '1');
    } catch (_) {
      /* stays closed for this visit anyway */
    }
  };

  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-white border-b border-[#E8E6DF]" role="region" aria-label="Get the app" data-testid="get-the-app-bar">
      <button type="button" onClick={close} aria-label="Close" className="p-1 -ml-1 text-[#8C9696] hover:text-[#2C3333]" data-testid="get-the-app-bar-close">
        <X size={16} aria-hidden="true" />
      </button>
      <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-xl flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#2C3333] leading-tight">Gr8ful Prayers</p>
        <p className="text-xs text-[#5C6666] leading-tight">Free on Google Play</p>
      </div>
      <a
        href={playStoreUrl('bar')}
        onClick={() => track(EVENTS.APP_STORE_LINK_CLICKED, { store: 'google', place: 'bar' })}
        className="rounded-full bg-[#4E8B7C] hover:bg-[#3D7265] text-white text-sm font-semibold px-4 py-1.5 flex-shrink-0"
        data-testid="get-the-app-bar-get"
      >
        Get
      </a>
    </div>
  );
}
